import { useEffect, useRef } from "react";
import { usePusher } from "../components/PusherProvider";
import type { PresenceChannel } from "pusher-js";

/**
 * Hook to subscribe to a Pusher presence channel
 * @param channelName - Name of the presence channel (e.g., 'presence-store123')
 * @returns The presence channel instance (for future use if needed)
 */
export const usePresenceChannel = (
	channelName: string,
): PresenceChannel | null => {
	const pusher = usePusher();
	const channelRef = useRef<PresenceChannel | null>(null);

	useEffect(() => {
		if (!pusher || !channelName) {
			return;
		}

		// Subscribe to presence channel
		const channel = pusher.subscribe(channelName) as PresenceChannel;
		channelRef.current = channel;

		// Cleanup: unsubscribe when component unmounts or channel changes
		return () => {
			if (channel) {
				pusher.unsubscribe(channelName);
				channelRef.current = null;
			}
		};
	}, [pusher, channelName]);

	return channelRef.current;
};
